import { useEffect, useMemo, useRef, useState } from "react";
import { Polyline, CircleMarker } from "react-leaflet";
import { DataPoint } from "./geolocationApi";
import { LatLng, LeafletMouseEvent } from "leaflet";
import { TooltipVehicleTrip } from "./tooltipVehicleTrip";
import { VehicleGeolocated } from "./index";
import {
  dateFormatterDayMonthYear2Digits,
  dateFormatterHourMinute,
} from "@qivia/ui";
import styled from "styled-components";
import { getClosestSegmentIndex } from "./getClosestSegmentIndex";

export const VehicleTripOnMap = (props: {
  showTripForVehicleUuid: string | null;
  isNotProductionEnvironment: () => boolean;
  fakeVehicleGeolocatedForDemo: VehicleGeolocated[];
  lastDataPointsVehiclesToDisplayed: VehicleGeolocated[] | null;
}) => {
  const {
    showTripForVehicleUuid,
    isNotProductionEnvironment,
    fakeVehicleGeolocatedForDemo,
    lastDataPointsVehiclesToDisplayed,
  } = props;

  const vehicleTripToDisplay = useMemo(() => {
    if (!showTripForVehicleUuid) return null;
    if (isNotProductionEnvironment()) {
      return fakeVehicleGeolocatedForDemo?.find(
        (v) => v.uuid === showTripForVehicleUuid,
      );
    } else {
      return lastDataPointsVehiclesToDisplayed?.find(
        (v) => v.uuid === showTripForVehicleUuid,
      );
    }
  }, [
    fakeVehicleGeolocatedForDemo,
    isNotProductionEnvironment,
    lastDataPointsVehiclesToDisplayed,
    showTripForVehicleUuid,
  ]);

  const polylineRef = useRef<L.Polyline | null>(null);
  const [hoverTripVehicleAndDataPoint, setHoverTripVehicleAndDataPoint] =
    useState<{
      vehicle: VehicleGeolocated;
      dataPoint: DataPoint;
    } | null>(null);
  const [mouseLatLng, setMouseLatLng] = useState<LatLng | null>(null);

  const dataPoints = useMemo(
    () => vehicleTripToDisplay?.dataPoints ?? [],
    [vehicleTripToDisplay?.dataPoints],
  );

  useEffect(() => {
    if (!vehicleTripToDisplay) return;
    const polyline = polylineRef.current;
    if (!polyline) return;
    const map = (polyline as unknown as { _map: L.Map })._map;
    if (!map) return;

    const latlngs = polyline.getLatLngs() as LatLng[];

    const onMove = (e: LeafletMouseEvent) => {
      //on set la souris en focntion de sa position sur la polyline
      setMouseLatLng(e.latlng);

      //on trouve le segment le plus proche de la souris
      const mouse = e.containerPoint;
      const segmentIndex = getClosestSegmentIndex(latlngs, mouse, map);

      setHoverTripVehicleAndDataPoint({
        vehicle: vehicleTripToDisplay,
        dataPoint: dataPoints[segmentIndex],
      });
    };

    const onLeave = () => {
      setMouseLatLng(null);
      setHoverTripVehicleAndDataPoint(null);
    };

    polyline.on("mousemove", onMove);
    polyline.on("mouseout", onLeave);

    return () => {
      polyline.off("mousemove", onMove);
      polyline.off("mouseout", onLeave);
    };
  }, [dataPoints, vehicleTripToDisplay]);

  return (
    <>
      <Polyline
        ref={polylineRef}
        positions={dataPoints.map((p) => [p.lat, p.lon])}
        color="black"
      />

      {mouseLatLng && hoverTripVehicleAndDataPoint && (
        <CircleMarker
          center={mouseLatLng}
          radius={5}
          color="black"
          fill={true}
          fillColor="black"
          fillOpacity={1}
          interactive={false}
        >
          <TooltipVehicleTrip
            vehicle={hoverTripVehicleAndDataPoint.vehicle}
            rightElement={
              <StyledHour>
                <div>
                  {dateFormatterDayMonthYear2Digits(
                    new Date(hoverTripVehicleAndDataPoint.dataPoint.date),
                  )}
                </div>
                {dateFormatterHourMinute(
                  new Date(hoverTripVehicleAndDataPoint.dataPoint.date),
                ).replaceAll(":", "h")}
              </StyledHour>
            }
            isPermanent={true}
          />
        </CircleMarker>
      )}
    </>
  );
};

const StyledHour = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
